import { type PseudoStateDataMap, type RuntimePseudoEvent, type RuntimePseudoState } from '../../types';
import { addEventListenerHover } from '../add-event-listener-hover';
import { checkableSelector } from '../pseudo-state-shared';

const pseudoStateEventCleanupMap = new WeakMap<HTMLElement, Map<string, WeakMap<HTMLElement, () => void>>>();

/**
 * Registers pseudo-state listeners for a list of trigger elements.
 *
 * @since ??
 *
 * @param {object} params Parameters.
 * @param {HTMLElement} params.element Target element being updated.
 * @param {NodeListOf<HTMLElement>|HTMLElement[]} params.hoverElements Trigger elements.
 * @param {PseudoStateDataMap<any>} params.stateDataMap State data map.
 * @param {CallableFunction} params.onPseudoStateChange Callback on pseudo state changes.
 * @param {string} params.listenerNamespace Namespace for listener cleanup scope.
 */
export const registerPseudoStateListeners = <TStateData>({
  element,
  hoverElements,
  stateDataMap,
  onPseudoStateChange,
  listenerNamespace = 'default',
}: {
  element: HTMLElement;
  hoverElements: NodeListOf<HTMLElement> | HTMLElement[];
  stateDataMap: PseudoStateDataMap<TStateData>;
  onPseudoStateChange: (
    hoverElement: HTMLElement,
    event?: RuntimePseudoEvent,
    forcedState?: RuntimePseudoState,
  ) => void;
  listenerNamespace?: string;
}): void => {
  hoverElements.forEach(hoverElement => {
    const cleanupByNamespace =
      pseudoStateEventCleanupMap.get(hoverElement) ?? new Map<string, WeakMap<HTMLElement, () => void>>();
    const cleanupByElement = cleanupByNamespace.get(listenerNamespace) ?? new WeakMap<HTMLElement, () => void>();
    const existingCleanup = cleanupByElement.get(element);

    if (existingCleanup) {
      existingCleanup();
    }

    const eventRegistrations: Array<{
      target: HTMLElement;
      eventName: string;
      handler: (event: Event) => void;
    }> = [];

    const registerEventListener = (target: HTMLElement, eventName: string, handler: (event: Event) => void): void => {
      target.addEventListener(eventName, handler);
      eventRegistrations.push({ target, eventName, handler });
    };

    addEventListenerHover(
      element,
      hoverElement,
      event => onPseudoStateChange(hoverElement, event, 'default'),
      event => onPseudoStateChange(hoverElement, event, 'hover'),
      `${listenerNamespace}:hover`,
    );

    if (null != stateDataMap.focusStateData || null != stateDataMap.activeStateData) {
      registerEventListener(hoverElement, 'focusin', event => onPseudoStateChange(hoverElement, event, 'focus'));
      registerEventListener(hoverElement, 'focusout', event => onPseudoStateChange(hoverElement, event));
    }

    if (null != stateDataMap.checkedStateData) {
      registerEventListener(hoverElement, 'change', event => onPseudoStateChange(hoverElement, event));

      hoverElement.querySelectorAll<HTMLElement>(checkableSelector).forEach(checkableElement => {
        registerEventListener(checkableElement, 'change', event => onPseudoStateChange(hoverElement, event));
      });
    }

    if (null != stateDataMap.activeStateData) {
      const activeEvents = [
        'mousedown',
        'mouseup',
        'touchstart',
        'touchend',
        'touchcancel',
        'pointerdown',
        'pointerup',
      ];

      activeEvents.forEach(eventName => {
        registerEventListener(hoverElement, eventName, event => onPseudoStateChange(hoverElement, event));
      });
    }

    cleanupByElement.set(element, () => {
      eventRegistrations.forEach(({ target, eventName, handler }) => {
        target.removeEventListener(eventName, handler);
      });
    });

    cleanupByNamespace.set(listenerNamespace, cleanupByElement);
    pseudoStateEventCleanupMap.set(hoverElement, cleanupByNamespace);
  });
};
